import {
  ActionConfirmationModal,
  Button,
  LoadingSpinner,
  SheetFooter,
  Typography,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { useUser } from "@/components/UserProvider";
import { DuplicateFileSlideover } from "@/components/features/matter/file-repo/DuplicateFileSlideover";
import { useFileEditor } from "@/lib/data/react-query/files/use-file-editor";
import { fileOperationMessage } from "@/lib/data/requests/files/canonical-files";
import { VITE_APRYSE_LICENSE_KEY } from "@/lib/data/static/importEnv";
import { SLIDEOVER_CONFIRM_CLOSE_DESCRIPTION } from "@/lib/data/static/slideoverStatics";
import type { MatterFile } from "@/lib/types";
import { formatUsersName } from "@/lib/utils/format";
import { useEffect, useRef, useState } from "react";
import { XIcon } from "lucide-react";
import toast from "react-hot-toast";

import { PDF_MIME_TYPE, PdfEditor, type PdfEditorHandle } from "./PdfEditor";

interface FileRepositoryPdfEditorSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  matterId: string;
  sourceFile: MatterFile;
}

export const FileRepositoryPdfEditorSlideover = ({
  open,
  setOpen,
  matterId,
  sourceFile,
}: FileRepositoryPdfEditorSlideoverProps) => {
  const { currentUser } = useUser();
  const annotationUserName = formatUsersName(currentUser)?.trim();
  const editorFile = useFileEditor(sourceFile.id, open);
  const editorRef = useRef<PdfEditorHandle | null>(null);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [isSaveAsOpen, setIsSaveAsOpen] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [isPdfSaving, setIsPdfSaving] = useState(false);
  const [hasAppliedRedactions, setHasAppliedRedactions] = useState(false);

  useEffect(() => {
    if (!open) {
      setIsEditorReady(false);
      setIsSaveAsOpen(false);
      setConfirmClose(false);
      setHasAppliedRedactions(false);
      setIsPdfSaving(false);
    }
  }, [open]);

  const handleSaveOriginal = async (blob: Blob) => {
    setIsPdfSaving(true);
    try {
      await editorFile.save.mutateAsync({ blob, contentType: PDF_MIME_TYPE });

      toast.success("Document saved");
      setOpen(false);
    } catch (error) {
      console.error("Failed to save PDF document", error);
      toast.error(fileOperationMessage(error, "Failed to save document"));
    } finally {
      setIsPdfSaving(false);
    }
  };

  const handleSavePdfAsCopy = async ({
    fileName,
    folderId,
  }: {
    fileName: string;
    folderId: string;
  }) => {
    if (!editorRef.current) {
      toast.error("PDF editor is not ready yet.");
      return;
    }

    try {
      const blob = await editorRef.current.exportDocument();
      if (!blob) {
        toast.error("Unable to export the document.");
        return;
      }

      await editorFile.copy.mutateAsync({
        blob,
        contentType: PDF_MIME_TYPE,
        name: fileName,
        folderId,
      });

      toast.success("Document saved as a copy.");
      setIsSaveAsOpen(false);
    } catch (error) {
      console.error("Failed to save PDF document copy", error);
      toast.error(fileOperationMessage(error, "Failed to save document copy."));
    }
  };

  const handleSaveOriginalFromFooter = async () => {
    if (!editorRef.current) {
      toast.error("PDF editor is not ready yet.");
      return;
    }

    const blob = await editorRef.current.exportDocument();
    if (!blob) {
      toast.error("Unable to export the document.");
      return;
    }

    await handleSaveOriginal(blob);
  };

  return (
    <>
      <AppModal
        open={open}
        onOpenChange={setOpen}
        isDirty
        hideHeader
        hideCloseButton
        title={`Edit ${sourceFile.name}`}
        description="Edit the PDF and save a new version or create a separate copy."
        bodyContained
        className="top-0 left-0 h-[100dvh] !max-h-[100dvh] !w-screen !max-w-none translate-x-0 translate-y-0 rounded-none"
      >
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="absolute top-4 right-4 z-10 h-8 w-8 p-0"
            onClick={() => {
              setConfirmClose(true);
            }}
          >
            <XIcon className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
          {editorFile.isLoading ? (
            <div className="flex min-h-0 flex-1 items-center justify-center px-4 py-4">
              <LoadingSpinner className="h-6 w-6" />
            </div>
          ) : editorFile.isError ? (
            <div className="flex min-h-0 flex-1 items-center justify-center px-6 py-4">
              <Typography className="text-destructive text-sm">
                Unable to load this document. Please try again.
              </Typography>
            </div>
          ) : (
            <PdfEditor
              ref={editorRef}
              key={`file-repo-pdf-editor-${sourceFile.id}`}
              fileName={sourceFile.name}
              initialDocUrl={editorFile.url}
              fileType="matter-file"
              annotationUserName={annotationUserName}
              licenseKey={VITE_APRYSE_LICENSE_KEY}
              onReadyChange={setIsEditorReady}
              onAppliedRedactionsChange={setHasAppliedRedactions}
              isActive={open && !!editorFile.url}
              showSaveButton={false}
              showHeader
              className="min-h-0 flex-1 px-4 py-4"
            />
          )}
          {!editorFile.isLoading &&
            !editorFile.isError &&
            !editorFile.canSave && (
              <p className="text-muted-foreground px-6 py-2 text-sm">
                {editorFile.saveUnavailableReason}
              </p>
            )}
          <SheetFooter addSlideoverStyles className="mt-auto shrink-0">
            <div className="flex grow items-center justify-between gap-x-2">
              {hasAppliedRedactions ? (
                <Typography className="text-muted-foreground text-sm">
                  Redactions were applied. Use save as to create a new file.
                </Typography>
              ) : (
                <div />
              )}
              <div className="flex justify-end gap-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setConfirmClose(true);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsSaveAsOpen(true);
                  }}
                  disabled={
                    editorFile.isLoading ||
                    editorFile.isError ||
                    !isEditorReady ||
                    isPdfSaving
                  }
                >
                  Save as
                </Button>
                <Button
                  type="button"
                  variant="light-primary"
                  onClick={() => {
                    void handleSaveOriginalFromFooter();
                  }}
                  disabled={
                    !editorFile.canSave ||
                    editorFile.isLoading ||
                    editorFile.isError ||
                    !isEditorReady ||
                    isPdfSaving ||
                    hasAppliedRedactions
                  }
                >
                  {isPdfSaving ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          </SheetFooter>
        </div>
      </AppModal>
      <DuplicateFileSlideover
        open={isSaveAsOpen}
        setOpen={setIsSaveAsOpen}
        matterId={matterId}
        sourceFile={sourceFile}
        title="Save as"
        description="Save a copy of this document with a new name."
        submitLabel="Save copy"
        onSaveAs={handleSavePdfAsCopy}
      />
      <ActionConfirmationModal
        open={confirmClose}
        setOpen={setConfirmClose}
        title="Unsaved changes"
        buttonText="Confirm"
        description={SLIDEOVER_CONFIRM_CLOSE_DESCRIPTION}
        afterActionFn={() => {
          setConfirmClose(false);
          setOpen(false);
        }}
      />
    </>
  );
};
